/*
 * Fleecora product page (A/B variant B) — small progressive enhancements.
 * Loaded as a module only on templates/product.fleecora.json.
 * Everything renders server-side first; this file only adds behaviour.
 *
 * Custom analytics events are published through Shopify's Customer Events API
 * (Shopify.analytics.publish) so a custom pixel or your A/B app can read them:
 *   fleecora:size_finder_opened, fleecora:size_recommended, fleecora:size_applied, fleecora:size_chart_opened,
 *   fleecora:size_chart_row_selected,
 *   fleecora:look_item_added, fleecora:look_all_added,
 *   fleecora:recs_viewed, fleecora:rec_clicked, fleecora:rec_quick_add_opened, fleecora:rec_quick_add,
 *   fleecora:rec_size_guide_opened
 */

/*
 * Works with both Horizon generations:
 *   Horizon 4.x  — @shopify/events: product:select (variant change), cart:lines-update (cart)
 *   Horizon 3.x  — @theme/events:   variant:update (variant change), cart:update (cart)
 * Whichever module the theme's import map provides is used; the other import just fails quietly.
 */
const hasImport = (name) => {
  try {
    const map = document.querySelector('script[type="importmap"]');
    return !!(map && JSON.parse(map.textContent).imports[name]);
  } catch (e) {
    return false;
  }
};
const eventsModule = hasImport('@shopify/events') ? import('@shopify/events').catch(() => null) : Promise.resolve(null);
const themeEventsModule = hasImport('@theme/events') ? import('@theme/events').catch(() => null) : Promise.resolve(null);

const publish = (name, data = {}) => {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (e) {
    /* analytics must never break the page */
  }
};

const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

// What to do after adding when the theme has no cart drawer (set in Fleecora page setup).
const noDrawerAction = () => document.querySelector('[data-fl-no-drawer]')?.dataset.flNoDrawer || 'cart';

/*
 * Adds lines to the cart, tells the theme about it (so the cart count and drawer contents
 * refresh), then opens the cart drawer. Horizon itself only opens the drawer when
 * Theme settings > Cart > "Auto-open cart drawer" is on; these buttons always open it.
 * Themes without a drawer go to the cart page instead (or stay, per Fleecora page setup).
 * Returns { ok, message }.
 */
async function addToCart(items, dispatchFrom, source) {
  const [mod, themeMod] = await Promise.all([eventsModule, themeEventsModule]);
  let deferred = null;
  if (mod && mod.CartLinesUpdateEvent) {
    deferred = mod.CartLinesUpdateEvent.createPromise();
    dispatchFrom.dispatchEvent(
      new mod.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
  }

  // Ask for the cart drawer's HTML in the same request, so it is filled in before it opens.
  const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const itemCount = items.reduce((n, i) => n + i.quantity, 0);
  let ok = false;
  let message = '';
  let sections;
  try {
    const res = await fetch(`${rootUrl()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(sectionIds.length ? { items, sections: sectionIds.join(',') } : { items }),
    });
    const body = await res.json();
    ok = res.ok && !body.status;
    if (!ok) message = body.description || body.message || 'Could not add to cart.';
    else sections = body.sections || undefined;
  } catch (e) {
    message = 'Could not add to cart. Please try again.';
  }

  let cart = null;
  try {
    cart = await (await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } })).json();
  } catch (e) {
    /* the add itself already succeeded or failed; the count just won't refresh */
  }

  if (deferred) {
    // Horizon 4.x
    if (cart) {
      deferred.resolve({
        cart: mod.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source, itemCount, didError: !ok, sections },
      });
    } else {
      deferred.reject(new Error('cart unavailable'));
    }
  } else if (ok) {
    // Horizon 3.x: the same CartAddEvent its product form sends ("cart:update").
    const data = { source, itemCount, sections };
    const Ev = themeMod && themeMod.CartAddEvent;
    const event = Ev
      ? new Ev(cart || undefined, source, data)
      : Object.assign(new Event('cart:update', { bubbles: true }), { detail: { resource: cart || undefined, sourceId: source, data } });
    dispatchFrom.dispatchEvent(event);
  }

  if (ok) afterAdd(dispatchFrom);
  return { ok, message };
}

/* Opens the theme's cart drawer. If the add came from inside an open pop-up (e.g. the size
   guide), the drawer opens once that pop-up has closed. No drawer: cart page, or stay. */
function afterAdd(from) {
  // The Fleecora cart drawer section opens itself on the cart event.
  const ownDrawer = document.querySelector('fleecora-cart-drawer');
  if (ownDrawer && !ownDrawer.hidden) return;
  const drawer =
    document.querySelector('theme-drawer#cart-drawer') || // Horizon 4.x
    document.querySelector('cart-drawer-component'); // Horizon 3.x
  const go = () => {
    if (!drawer) {
      if (noDrawerAction() === 'cart') window.location.href = `${rootUrl()}cart`;
      return;
    }
    const tag = drawer.tagName.toLowerCase();
    customElements.whenDefined(tag).then(() => {
      // Let the theme finish refreshing the drawer contents first.
      requestAnimationFrame(() => {
        if (drawer.hasAttribute('open') || drawer.querySelector('dialog[open]')) return;
        if (typeof drawer.open === 'function') drawer.open();
        else if (typeof drawer.showDialog === 'function') drawer.showDialog();
        else drawer.querySelector('dialog')?.showModal?.();
      });
    });
  };
  const modal = from instanceof Element ? from.closest('dialog:modal') : null;
  if (modal && modal.open) {
    modal.addEventListener('close', go, { once: true });
    modal.close();
  } else {
    go();
  }
}

/* ------------------------------------------------------------------ */
/* <fleecora-sync data-fl-id="…">                                       */
/* Re-renders its contents when the variant picker changes the selected */
/* variant. Horizon fetches the page HTML for the new variant and hands  */
/* it over in its variant event (product:select in 4.x, variant:update  */
/* in 3.x); the matching <fleecora-sync> is copied from it.             */
/* ------------------------------------------------------------------ */
class FleecoraSync extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    if (!this.section) return;
    this.section.addEventListener('product:select', this.onSelect);
    this.section.addEventListener('variant:update', this.onUpdate);
  }

  disconnectedCallback() {
    if (!this.section) return;
    this.section.removeEventListener('product:select', this.onSelect);
    this.section.removeEventListener('variant:update', this.onUpdate);
  }

  fromCard = (event) => event.target instanceof Element && !!event.target.closest('product-card, fleecora-quick-add, quick-add-dialog');

  apply(html) {
    if (!html || typeof html.querySelector !== 'function') return;
    const next = html.querySelector(`fleecora-sync[data-fl-id="${this.dataset.flId}"]`);
    if (next) this.innerHTML = next.innerHTML;
  }

  // Horizon 4.x: the HTML arrives on the event's promise.
  onSelect = (event) => {
    if (this.fromCard(event)) return;
    event.promise?.then(({ detail }) => this.apply(detail && detail.html)).catch(() => {});
  };

  // Horizon 3.x: the HTML is on the event itself.
  onUpdate = (event) => {
    if (this.fromCard(event)) return;
    const data = event.detail && event.detail.data;
    if (data && data.productId && this.dataset.productId && String(data.productId) !== String(this.dataset.productId)) return;
    this.apply(data && data.html);
  };
}

/* ------------------------------------------------------------------ */
/* <fleecora-delivery data-min="5" data-max="9" data-cutoff="14">       */
/* ------------------------------------------------------------------ */
class FleecoraDelivery extends HTMLElement {
  connectedCallback() {
    const out = this.querySelector('[data-fl-dates]');
    if (!out) return;
    const min = parseInt(this.dataset.min, 10) || 0;
    const max = parseInt(this.dataset.max, 10) || min;
    const cutoff = parseInt(this.dataset.cutoff, 10);
    const skipWeekends = this.dataset.skipWeekends !== 'false';

    // Work in the warehouse's time zone (UK), so the cut-off and "today" match the shipping policy
    // wherever the shopper is.
    const tz = this.dataset.timezone || 'Europe/London';
    let start = new Date();
    let hour = start.getHours();
    try {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23' })
          .formatToParts(start)
          .map((p) => [p.type, p.value])
      );
      start = new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12);
      hour = Number(parts.hour);
    } catch (e) {
      /* old browser: fall back to the shopper's clock */
    }
    // Orders after the cut-off, or at the weekend, are processed on the next working day.
    const weekend = start.getDay() === 0 || start.getDay() === 6;
    const afterCutoff = !Number.isNaN(cutoff) && cutoff < 24 && hour >= cutoff;
    const extra = afterCutoff || (skipWeekends && weekend) ? 1 : 0;

    const addDays = (n) => {
      const d = new Date(start);
      let count = 0;
      while (count < n) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (!skipWeekends || (day !== 0 && day !== 6)) count += 1;
      }
      return d;
    };

    const lang = document.documentElement.lang || 'en-GB';
    let fmt;
    try {
      fmt = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short' });
    } catch (e) {
      fmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    }
    const from = fmt.format(addDays(min + extra));
    const to = fmt.format(addDays(max + extra));
    out.textContent = min === max ? from : `${from} – ${to}`;
    this.setAttribute('data-ready', '');
  }
}

/* ------------------------------------------------------------------ */
/* <fleecora-size-finder data-option-name="Size">                       */
/* ------------------------------------------------------------------ */
class FleecoraSizeFinder extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-fl-toggle]');
    this.panel = this.querySelector('[data-fl-panel]');
    this.rec = this.querySelector('[data-fl-rec]');
    this.hint = this.querySelector('[data-fl-hint]');
    this.recSize = this.querySelector('[data-fl-rec-size]');
    this.recNote = this.querySelector('[data-fl-rec-note]');
    this.apply = this.querySelector('[data-fl-apply]');
    this.state = { height: null, heightNote: '', usual: null };

    this.toggle?.addEventListener('click', () => {
      const open = this.panel.hidden;
      this.panel.hidden = !open;
      this.toggle.setAttribute('aria-expanded', String(open));
      if (open) publish('fleecora:size_finder_opened', { product_id: this.dataset.productId });
    });

    this.querySelectorAll('[data-fl-choice]').forEach((btn) => {
      btn.addEventListener('click', () => this.choose(btn));
    });

    this.apply?.addEventListener('click', () => this.applySize());
    this.setupChart();
  }

  /* Size chart dialog: open/close, cm/in toggle, highlight the selected size. */
  setupChart() {
    this.chart = this.querySelector('[data-fl-chart]');
    if (!this.chart) return;
    this.querySelectorAll('[data-fl-chart-open]').forEach((btn) =>
      btn.addEventListener('click', () => this.openChart(btn))
    );
    this.chart.querySelector('[data-fl-chart-close]')?.addEventListener('click', () => this.chart.close());
    // Click on the backdrop closes it.
    this.chart.addEventListener('click', (e) => {
      if (e.target === this.chart) this.chart.close();
    });
    this.chart.addEventListener('close', () => this.lastOpener?.focus({ preventScroll: true }));
    this.chart.querySelectorAll('[data-fl-unit-btn]').forEach((btn) =>
      btn.addEventListener('click', () => this.setUnit(btn.dataset.flUnitBtn))
    );
    // Tap anywhere on a row to pick that size (the size cell holds the real button for keyboards).
    this.chart.querySelector('.fl-chart__table--select tbody')?.addEventListener('click', (e) => {
      const row = e.target instanceof Element ? e.target.closest('tr[data-fl-size]') : null;
      if (row) this.pickRow(row.dataset.flSize);
    });
  }

  sameSize(a, b) {
    return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  }

  // Marks which sizes exist / are sold out, from Horizon's variant picker.
  refreshAvailability() {
    this.chart.querySelectorAll('tr[data-fl-size]').forEach((row) => {
      const input = this.findInput(row.dataset.flSize);
      const soldOut = !input || input.dataset.optionAvailable === 'false';
      row.classList.toggle('is-soldout', soldOut);
      const badge = row.querySelector('[data-fl-soldout]');
      if (badge) {
        badge.hidden = !soldOut;
        badge.textContent = input ? 'Sold out' : 'Not available';
      }
    });
  }

  // Highlights a row, updates the diagram and the status line.
  markChart(size, message) {
    this.chart.querySelectorAll('tr[data-fl-size]').forEach((row) => {
      const on = !!size && this.sameSize(row.dataset.flSize, size);
      row.classList.toggle('is-current', on);
      if (on) row.setAttribute('aria-current', 'true');
      else row.removeAttribute('aria-current');
      row.querySelector('[data-fl-pick]')?.setAttribute('aria-pressed', String(on));
    });
    const status = this.chart.querySelector('[data-fl-chart-status]');
    if (status && message !== undefined) status.textContent = message;
  }

  pickRow(size) {
    const label = (this.dataset.optionName || 'Size');
    if (this.selectSize(size)) {
      this.markChart(size, `${label} ${size} selected`);
      publish('fleecora:size_chart_row_selected', { product_id: this.dataset.productId, size });
    } else {
      const exists = !!this.findInput(size);
      this.markChart(size, exists ? `${size} is sold out right now` : `${size} isn't available in this style`);
    }
  }

  openChart(opener) {
    this.lastOpener = opener;
    const current = (this.querySelector('[data-fl-current-size]')?.dataset.flCurrentSize || '').trim();
    this.refreshAvailability();
    this.markChart(current, '');
    if (typeof this.chart.showModal === 'function') this.chart.showModal();
    else this.chart.setAttribute('open', '');
    publish('fleecora:size_chart_opened', { product_id: this.dataset.productId, size: current });
  }

  setUnit(unit) {
    this.chart.querySelectorAll('[data-fl-unit-btn]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.flUnitBtn === unit)));
    this.chart.querySelectorAll('td[data-cm]').forEach((td) => {
      td.textContent = unit === 'in' ? td.dataset.in : td.dataset.cm;
    });
    this.chart.querySelectorAll('[data-fl-unit]').forEach((u) => {
      u.textContent = unit === 'in' ? '(in)' : '(cm)';
    });
  }

  choose(btn) {
    const group = btn.dataset.flChoice;
    this.querySelectorAll(`[data-fl-choice="${group}"]`).forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    if (group === 'height') {
      this.state.height = btn.dataset.value;
      this.state.heightNote = btn.dataset.note || '';
    } else {
      this.state.usual = btn.dataset.value;
    }
    this.render();
  }

  render() {
    const size = this.state.usual;
    if (!size || !this.state.height) {
      this.rec.hidden = true;
      if (this.hint) this.hint.hidden = false;
      return;
    }
    this.recSize.textContent = size;
    this.recNote.textContent = this.state.heightNote || this.dataset.defaultNote || '';
    this.apply.textContent = `${this.dataset.selectLabel || 'Select'} ${size}`;
    const input = this.findInput(size);
    const available = !!input && input.dataset.optionAvailable !== 'false' && !input.disabled;
    this.apply.disabled = !available;
    if (!available && this.dataset.unavailableNote) this.recNote.textContent = this.dataset.unavailableNote;
    this.rec.hidden = false;
    if (this.hint) this.hint.hidden = true;
    publish('fleecora:size_recommended', { product_id: this.dataset.productId, size, height: this.state.height });
  }

  findInput(size) {
    const section = this.closest('.shopify-section') || document;
    const name = (this.dataset.optionName || 'Size').toLowerCase();
    const inputs = section.querySelectorAll('variant-picker input[type="radio"], variant-picker select option');
    for (const input of inputs) {
      const optionName = (input.dataset.optionName || input.closest('select')?.dataset.optionName || '').toLowerCase();
      if (optionName && optionName !== name) continue;
      if (String(input.value).trim().toLowerCase() === String(size).trim().toLowerCase()) return input;
    }
    return null;
  }

  // Selects a size in Horizon's variant picker. Returns false if it doesn't exist or is sold out.
  selectSize(size) {
    const input = this.findInput(size);
    if (!input || input.dataset.optionAvailable === 'false') return false;
    if (input.tagName === 'OPTION') {
      const select = input.closest('select');
      select.value = input.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (!input.checked) {
      input.click();
    }
    return true;
  }

  applySize() {
    const size = this.state.usual;
    const input = this.findInput(size);
    if (!this.selectSize(size)) return;
    this.panel.hidden = true;
    this.toggle?.setAttribute('aria-expanded', 'false');
    publish('fleecora:size_applied', { product_id: this.dataset.productId, size });
    input.closest('fieldset, .variant-option')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

/* ------------------------------------------------------------------ */
/* <fleecora-look data-product-id="…"> — Complete the look               */
/* ------------------------------------------------------------------ */
class FleecoraLook extends HTMLElement {
  connectedCallback() {
    this.error = this.querySelector('[data-fl-look-error]');
    // Horizon pre-selects the first available size. Only trust the selection for
    // "Add the look" once the shopper has picked a size (or arrived on a ?variant= URL).
    this.choiceMade = new URLSearchParams(window.location.search).has('variant');
    document.addEventListener('change', (e) => {
      if (e.target instanceof Element && e.target.closest('variant-picker')) this.choiceMade = true;
    });
    this.querySelectorAll('[data-fl-add]').forEach((btn) => {
      btn.addEventListener('click', () => this.addOne(btn));
    });
    this.querySelector('[data-fl-add-all]')?.addEventListener('click', (e) => this.addAll(e.currentTarget));
  }

  variantFor(item) {
    const select = item.querySelector('select[data-fl-variant]');
    if (select) return select.value;
    return item.dataset.variantId;
  }

  mainVariantId() {
    const pid = this.dataset.productId;
    const input = document.querySelector(
      `product-form-component[data-product-id="${pid}"] input[name="id"], form[data-type="add-to-cart-form"] input[name="id"]`
    );
    return input ? input.value : this.dataset.mainVariantId;
  }

  async addOne(btn) {
    const item = btn.closest('[data-fl-item]');
    const id = this.variantFor(item);
    if (!id) return;
    const ok = await this.add([{ id: Number(id), quantity: 1 }], btn);
    if (ok) {
      btn.dataset.added = 'true';
      btn.textContent = this.dataset.addedLabel || 'Added';
      publish('fleecora:look_item_added', { product_id: this.dataset.productId, variant_id: id });
    }
  }

  async addAll(btn) {
    const items = [];
    if (this.dataset.includeMain !== 'false' && this.dataset.needsChoice === 'true' && !this.choiceMade) {
      if (this.error) this.error.textContent = this.dataset.chooseMessage || 'Choose your size above first.';
      const picker = document.querySelector('#MainContent variant-picker');
      if (picker) {
        picker.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const first = picker.querySelector('input:checked, input, select');
        if (first) setTimeout(() => first.focus({ preventScroll: true }), 400);
      }
      return;
    }
    if (this.dataset.includeMain !== 'false') {
      const mainId = this.mainVariantId();
      if (mainId) items.push({ id: Number(mainId), quantity: 1 });
    }
    this.querySelectorAll('[data-fl-item]').forEach((item) => {
      if (item.dataset.available === 'false') return;
      const id = this.variantFor(item);
      if (id) items.push({ id: Number(id), quantity: 1 });
    });
    if (!items.length) return;
    const ok = await this.add(items, btn);
    if (ok) {
      this.querySelectorAll('[data-fl-add]').forEach((b) => {
        b.dataset.added = 'true';
        b.textContent = this.dataset.addedLabel || 'Added';
      });
      publish('fleecora:look_all_added', { product_id: this.dataset.productId, lines: items.length });
    }
  }

  async add(items, btn) {
    if (this.error) this.error.textContent = '';
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    const { ok, message } = await addToCart(items, this, 'fleecora-look');
    if (!ok && this.error) this.error.textContent = message;
    btn.removeAttribute('aria-busy');
    btn.disabled = false;
    return ok;
  }
}

/* ------------------------------------------------------------------ */
/* <fleecora-recs> — "You may also like"                                */
/* Loads Shopify product recommendations when the section nears the    */
/* viewport, then swaps them in. Hides itself when there are none.     */
/* ------------------------------------------------------------------ */
class FleecoraRecs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    if (this.dataset.performed === 'true') {
      this.afterRender();
      return;
    }
    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    const { url, productId, sectionId, intent } = this.dataset;
    if (!url || !productId || !sectionId) return this.hide();
    try {
      const res = await fetch(`${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent || 'related'}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('fleecora-recs');
      if (!next || !next.querySelector('[data-fl-rec-link]')) return this.hide();
      this.innerHTML = next.innerHTML;
      this.dataset.performed = 'true';
      this.dataset.source = next.dataset.source || '';
      this.removeAttribute('data-loading');
      this.afterRender();
    } catch (e) {
      if (!window.Shopify?.designMode) this.hide();
    }
  }

  hide() {
    const target = this.closest('.shopify-section') || this;
    target.setAttribute('hidden', '');
    target.style.display = 'none';
  }

  afterRender() {
    const limit = parseInt(this.dataset.limit, 10) || Infinity;
    const cards = [...this.querySelectorAll('[data-fl-rec-link]')];
    let kept = 0;
    cards.forEach((link) => {
      const card = link.closest('.fl-card');
      if (link.dataset.productId === this.dataset.productId || kept >= limit) {
        card?.remove();
      } else {
        kept += 1;
        link.dataset.position = String(kept);
        card?.classList.toggle('fl-card--m-extra', kept > (parseInt(this.dataset.mobileMax, 10) || Infinity));
        const qa = card?.querySelector('fleecora-quick-add');
        if (qa) qa.dataset.position = String(kept);
      }
    });
    const links = this.querySelectorAll('[data-fl-rec-link]');
    if (!links.length) return this.hide();
    publish('fleecora:recs_viewed', {
      product_id: this.dataset.productId,
      source: this.dataset.source,
      count: links.length,
    });
  }

  onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-fl-rec-link]') : null;
    if (!link) return;
    publish('fleecora:rec_clicked', {
      product_id: this.dataset.productId,
      clicked_product_id: link.dataset.productId,
      position: Number(link.dataset.position),
      source: this.dataset.source,
    });
  };
}

/* Shared size-chart helpers (used by the You may also like size guide) */
function setChartUnit(root, unit) {
  root.querySelectorAll('[data-fl-unit-btn]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.flUnitBtn === unit)));
  root.querySelectorAll('td[data-cm]').forEach((td) => {
    td.textContent = unit === 'in' ? td.dataset.in : td.dataset.cm;
  });
  root.querySelectorAll('[data-fl-unit]').forEach((u) => {
    u.textContent = unit === 'in' ? '(in)' : '(cm)';
  });
}

function initGuideDialog(dialog) {
  if (dialog.flReady) return;
  dialog.flReady = true;
  dialog.querySelector('[data-fl-chart-close]')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) {
      dialog.close();
      return;
    }
    const unitBtn = e.target instanceof Element ? e.target.closest('[data-fl-unit-btn]') : null;
    if (unitBtn) {
      setChartUnit(dialog, unitBtn.dataset.flUnitBtn);
      return;
    }
    const row = e.target instanceof Element ? e.target.closest('tbody tr[data-fl-size]') : null;
    if (row && dialog.flOwner) dialog.flOwner.pickFromGuide(row.dataset.flSize);
  });
  dialog.addEventListener('close', () => dialog.flOwner?.button?.focus({ preventScroll: true }));
}

/* ------------------------------------------------------------------ */
/* <fleecora-quick-add> — "+ Add" on You may also like cards           */
/* Opens the product's options (colour pre-picked to match the photo,  */
/* size left for the shopper), then Add to cart.                       */
/* ------------------------------------------------------------------ */
const SIZE_NAMES = ['size', 'taille', 'größe', 'talla', 'taglia'];

class FleecoraQuickAdd extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.openBtn = this.querySelector('[data-fl-qa-open]');
    this.panel = this.querySelector('[data-fl-qa-panel]');
    this.atc = this.querySelector('[data-fl-qa-atc]');
    this.label = this.querySelector('[data-fl-qa-label]');
    this.msg = this.querySelector('[data-fl-qa-msg]');
    this.groups = [...this.querySelectorAll('[data-fl-qa-group]')];

    if (this.dataset.single === 'true') {
      this.atc?.addEventListener('click', () => this.add(this.atc.dataset.variantId));
      return;
    }

    try {
      this.variants = JSON.parse(this.querySelector('[data-fl-qa-variants]').textContent);
    } catch (e) {
      this.variants = [];
    }

    // Pre-pick options that don't need a decision: single-value options, and non-size
    // options (e.g. colour) set to the variant whose photo the card shows.
    let start = [];
    try {
      start = JSON.parse(this.dataset.start || '[]');
    } catch (e) {
      start = [];
    }
    this.selected = this.groups.map((g, i) => {
      const values = [...g.querySelectorAll('[data-fl-qa-value]')].map((b) => b.dataset.flQaValue);
      if (values.length === 1) return values[0];
      if (!this.isSizeGroup(g) && this.groups.some((x) => this.isSizeGroup(x))) return start[i] ?? null;
      return null;
    });

    this.openBtn?.addEventListener('click', () => this.toggle());
    this.groups.forEach((g, i) => {
      g.querySelectorAll('[data-fl-qa-value]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.getAttribute('aria-disabled') === 'true') return;
          this.selected[i] = btn.dataset.flQaValue;
          // A new colour may make the chosen size unavailable: clear it if so.
          this.groups.forEach((_, j) => {
            if (j !== i && this.selected[j] && !this.optionAvailable(j, this.selected[j])) this.selected[j] = null;
          });
          this.render();
        });
      });
    });
    this.atc?.addEventListener('click', () => {
      const v = this.current();
      if (v && v.a) this.add(v.id);
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.panel && !this.panel.hidden) {
        this.toggle(false);
        this.openBtn?.focus();
      }
    });
    this.sizeIdx = this.groups.findIndex((g) => this.isSizeGroup(g));
    this.measureEl = this.querySelector('[data-fl-qa-measure]');
    this.chartTpl = this.closest('.fl-card')?.querySelector('[data-fl-qa-chart]') || null;
    this.render();
  }

  /* ---- Measurements line + size guide (from this product's own size chart) ---- */

  chartRow(size) {
    if (!this.chartTpl || !size) return null;
    const key = String(size).trim().toLowerCase();
    return [...this.chartTpl.content.querySelectorAll('tr[data-fl-size]')].find(
      (r) => r.dataset.flSize.trim().toLowerCase() === key
    ) || null;
  }

  chartHeaders() {
    return [...this.chartTpl.content.querySelectorAll('thead th')].map((th) =>
      th.textContent.replace(/\((cm|in)\)/, '').trim()
    );
  }

  renderMeasure() {
    if (!this.measureEl) return;
    const size = this.sizeIdx >= 0 ? this.selected[this.sizeIdx] : null;
    const row = this.chartRow(size);
    this.measureEl.textContent = '';
    if (!row) {
      this.measureEl.hidden = true;
      return;
    }
    const headers = this.chartHeaders();
    const wanted = (this.dataset.measureCols || '').split(',').map((w) => w.trim().toLowerCase()).filter(Boolean);
    const cells = [...row.children];
    const strong = document.createElement('strong');
    strong.textContent = `${this.groups[this.sizeIdx].dataset.name || 'Size'}: ${size}`;
    this.measureEl.append(strong);
    wanted.forEach((w) => {
      const i = headers.findIndex((h) => h.toLowerCase() === w);
      const cell = i > 0 ? cells[i] : null;
      if (!cell) return;
      const cm = (cell.dataset.cm || cell.textContent).trim();
      if (!cm) return;
      let text = `${headers[i]}: ${cm}`;
      if (cell.dataset.cm && /\d/.test(cm)) {
        text += ' cm';
        if (cell.dataset.in && cell.dataset.in !== cell.dataset.cm) text += ` (${cell.dataset.in} in)`;
      }
      this.measureEl.append(` · ${text}`);
    });
    this.measureEl.hidden = false;
  }

  // Chart rows are matched to the card's size buttons case-insensitively.
  optionValueFor(chartSize) {
    if (this.sizeIdx < 0) return null;
    const key = String(chartSize).trim().toLowerCase();
    const btn = [...this.groups[this.sizeIdx].querySelectorAll('[data-fl-qa-value]')].find(
      (b) => b.dataset.flQaValue.trim().toLowerCase() === key
    );
    return btn ? btn.dataset.flQaValue : null;
  }

  // Is this card's picker usable right now (quick add can be hidden on phones)?
  get usable() {
    return this.sizeIdx >= 0 && !!this.variants && getComputedStyle(this).display !== 'none';
  }

  // State of a size-chart row for the size guide: matching button value, sold out, currently picked.
  sizeState(chartSize) {
    const value = this.optionValueFor(chartSize);
    return {
      value,
      available: !!value && this.optionAvailable(this.sizeIdx, value),
      current: !!value && this.selected[this.sizeIdx] === value,
    };
  }

  // Pick a size from the size guide; opens the panel so Add to cart is right there.
  chooseFromChart(chartSize) {
    const st = this.sizeState(chartSize);
    if (!st.available) return st.value ? `${chartSize} is sold out right now` : `${chartSize} isn't available in this style`;
    this.selected[this.sizeIdx] = st.value;
    if (this.panel?.hidden) {
      this.closeOthers();
      this.panel.hidden = false;
      this.openBtn?.setAttribute('aria-expanded', 'true');
      this.closest('.fl-card')?.classList.add('fl-card--qa-open');
    }
    this.render();
    return `Size ${st.value} selected`;
  }

  isSizeGroup(g) {
    return SIZE_NAMES.includes((g.dataset.name || '').trim().toLowerCase());
  }

  // Close every other open card in this section, so only one panel is open at a time.
  closeOthers() {
    const scope = this.closest('fleecora-recs') || document;
    scope.querySelectorAll('fleecora-quick-add').forEach((qa) => {
      if (qa !== this && qa.panel && !qa.panel.hidden) qa.toggle(false);
    });
  }

  toggle(force) {
    const open = typeof force === 'boolean' ? force : this.panel.hidden;
    if (open) this.closeOthers();
    this.panel.hidden = !open;
    this.openBtn.setAttribute('aria-expanded', String(open));
    this.closest('.fl-card')?.classList.toggle('fl-card--qa-open', open);
    if (open) {
      publish('fleecora:rec_quick_add_opened', {
        product_id: this.closest('fleecora-recs')?.dataset.productId,
        opened_product_id: this.dataset.productId,
        position: Number(this.dataset.position),
      });
      const first = this.panel.querySelector('.fl-qa__pill:not([aria-disabled="true"])');
      first?.focus({ preventScroll: true });
    }
  }

  // Is there an available variant with option j = value, given the picks in the options
  // before it (Shopify's convention: colour is never blocked by size, size depends on colour)?
  optionAvailable(j, value) {
    return this.variants.some(
      (v) => v.a && v.o[j] === value && v.o.every((val, k) => k >= j || !this.selected[k] || this.selected[k] === val)
    );
  }

  current() {
    if (this.selected.some((x) => x == null)) return null;
    return this.variants.find((v) => v.o.every((val, k) => val === this.selected[k])) || null;
  }

  render() {
    this.groups.forEach((g, i) => {
      const chosen = g.querySelector('[data-fl-qa-chosen]');
      if (chosen) chosen.textContent = this.selected[i] ? `: ${this.selected[i]}` : '';
      g.querySelectorAll('[data-fl-qa-value]').forEach((btn) => {
        const value = btn.dataset.flQaValue;
        btn.setAttribute('aria-pressed', String(this.selected[i] === value));
        const available = this.optionAvailable(i, value);
        btn.setAttribute('aria-disabled', String(!available));
        btn.classList.toggle('fl-qa__pill--off', !available);
        const base = value + (available ? '' : ' (sold out)');
        btn.setAttribute('aria-label', base);
      });
    });

    const v = this.current();
    const missing = this.groups.find((g, i) => this.selected[i] == null);
    if (v && v.a) {
      this.atc.disabled = false;
      this.label.textContent = 'Add to cart';
      const price = document.createElement('span');
      price.className = 'fl-qa__price';
      price.textContent = ` · ${v.p}`;
      this.label.append(price);
    } else {
      this.atc.disabled = true;
      this.label.textContent = missing
        ? `Choose a ${(missing.dataset.name || 'size').toLowerCase()}`
        : 'Sold out';
    }
    this.renderMeasure?.();
  }

  async add(variantId) {
    if (!variantId || !this.atc) return;
    const labelEl = this.label || this.atc;
    const before = labelEl.textContent;
    this.atc.setAttribute('aria-busy', 'true');
    this.atc.disabled = true;
    if (this.msg) this.msg.textContent = '';
    const { ok, message } = await addToCart([{ id: Number(variantId), quantity: 1 }], this, 'fleecora-recs');
    this.atc.removeAttribute('aria-busy');
    if (ok) {
      labelEl.textContent = 'Added ✓';
      publish('fleecora:rec_quick_add', {
        product_id: this.closest('fleecora-recs')?.dataset.productId,
        added_product_id: this.dataset.productId,
        variant_id: String(variantId),
        position: Number(this.dataset.position),
      });
      // The cart drawer opens now, so fold the panel straight away and put focus on
      // "+ Add"; closing the drawer then returns focus there instead of a hidden button.
      if (this.panel) this.toggle(false);
      this.openBtn?.focus({ preventScroll: true });
      setTimeout(() => {
        labelEl.textContent = before;
        this.atc.disabled = false;
      }, 1500);
    } else {
      labelEl.textContent = before;
      this.atc.disabled = false;
      if (this.msg) this.msg.textContent = message;
    }
  }
}

/* ------------------------------------------------------------------ */
/* <fleecora-card-guide> — Size guide button on You may also like cards */
/* Opens the product's own size chart in the section's shared dialog.  */
/* Tapping a row picks that size in the card's "+ Add" panel when the  */
/* panel is available; otherwise the chart is view-only.               */
/* ------------------------------------------------------------------ */
class FleecoraCardGuide extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.button = this.querySelector('[data-fl-card-guide]');
    this.button?.addEventListener('click', () => this.open());
  }

  get card() {
    return this.closest('.fl-card');
  }

  get quickAdd() {
    const qa = this.card?.querySelector('fleecora-quick-add');
    return qa && qa.usable ? qa : null;
  }

  get dialog() {
    return this.closest('fleecora-recs')?.querySelector('[data-fl-recs-chart]') || null;
  }

  open() {
    const dialog = this.dialog;
    const tpl = this.card?.querySelector('[data-fl-qa-chart]');
    if (!dialog || !tpl) return;
    initGuideDialog(dialog);
    dialog.flOwner = this;
    const parts = tpl.content;
    dialog.querySelector('[data-fl-guide-product]').textContent = this.dataset.title || '';
    const tableSlot = dialog.querySelector('[data-fl-guide-table]');
    const figureSlot = dialog.querySelector('[data-fl-guide-figure]');
    tableSlot.replaceChildren(...[...parts.querySelector('[data-part="table"]').childNodes].map((n) => n.cloneNode(true)));
    figureSlot.replaceChildren(...[...parts.querySelector('[data-part="figure"]').childNodes].map((n) => n.cloneNode(true)));
    const units = dialog.querySelector('[data-fl-guide-units]');
    if (units) units.hidden = !tableSlot.querySelector('td[data-cm]');
    const selectable = !!this.quickAdd;
    dialog.classList.toggle('fl-chart--readonly', !selectable);
    const hint = dialog.querySelector('[data-fl-guide-hint]');
    if (hint) hint.hidden = !selectable;
    setChartUnit(dialog, 'cm');
    this.mark('');
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    publish('fleecora:rec_size_guide_opened', {
      product_id: this.closest('fleecora-recs')?.dataset.productId,
      opened_product_id: this.dataset.productId,
    });
  }

  mark(message) {
    const dialog = this.dialog;
    if (!dialog) return;
    const qa = this.quickAdd;
    dialog.querySelectorAll('tr[data-fl-size]').forEach((row) => {
      const st = qa ? qa.sizeState(row.dataset.flSize) : { value: row.dataset.flSize, available: true, current: false };
      row.classList.toggle('is-current', st.current);
      row.classList.toggle('is-soldout', !!qa && !st.available);
      const pick = row.querySelector('[data-fl-pick]');
      if (pick) {
        pick.setAttribute('aria-pressed', String(st.current));
        pick.disabled = !qa;
      }
      const badge = row.querySelector('[data-fl-soldout]');
      if (badge) {
        badge.hidden = !qa || st.available;
        badge.textContent = st.value ? 'Sold out' : 'Not available';
      }
    });
    const status = dialog.querySelector('[data-fl-chart-status]');
    if (status && message !== undefined) status.textContent = message;
  }

  pickFromGuide(chartSize) {
    const qa = this.quickAdd;
    if (!qa) return;
    this.mark(qa.chooseFromChart(chartSize));
  }
}

const define = (name, cls) => {
  if (!customElements.get(name)) customElements.define(name, cls);
};
define('fleecora-sync', FleecoraSync);
define('fleecora-delivery', FleecoraDelivery);
define('fleecora-size-finder', FleecoraSizeFinder);
define('fleecora-look', FleecoraLook);
define('fleecora-recs', FleecoraRecs);
define('fleecora-quick-add', FleecoraQuickAdd);
define('fleecora-card-guide', FleecoraCardGuide);
